import { useMutation, useQuery, useQueryClient } from 'react-query'
import { ArrowLeft, X, Edit, Plus, Clock, Users, Calendar, Save, XCircle, Loader2 } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from '../utils/toastUtils'
import api from '../utils/axios'

const OrderscheduleEdit = () => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { id } = useParams()

    const [formData, setFormData] = useState({
        vendor_id: '',
        start_date: '',
        end_date: '',
        is_active: true,
        min_lead_time_minutes: '',
        time_slot_duration: '15',
        slots: [{
            start_time: '',
            end_time: '',
            slot_limit: '5',
            slot_status: 'Open'
        }]
    })

    const [addedSlots, setAddedSlots] = useState([])
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('')

    // Fetch existing schedule data
    const { data: scheduleData, isLoading } = useQuery({
        queryKey: ['order-schedule', id],
        queryFn: async () => {
            const response = await api.get(`/api/order-schedule/${id}`);
            return response.data;
        },
        enabled: !!id,
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to load schedule')
        }
    });

    // Initialize form with existing data
    useEffect(() => {
        if (scheduleData?.data?.schedule || scheduleData?.order_schedule || scheduleData?.schedule) {
            const schedule = scheduleData.schedule || scheduleData.order_schedule || scheduleData.data?.schedule

            // Format dates for datetime-local input
            const formatDateForInput = (dateString) => {
                if (!dateString) return ''
                const date = new Date(dateString)
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                const hours = String(date.getHours()).padStart(2, '0')
                const minutes = String(date.getMinutes()).padStart(2, '0')
                
                return `${year}-${month}-${day}T${hours}:${minutes}`
            }

            // Load existing slots into addedSlots
            const existingSlots = schedule.slots ? schedule.slots.map(slot => ({
                id: slot.id,
                start_time: slot.start_time,
                end_time: slot.end_time,
                slot_limit: slot.slot_limit?.toString() || '',
                slot_status: slot.slot_status || 'Open',
                duration: calculateDuration(slot.start_time, slot.end_time)
            })) : []

            setAddedSlots(existingSlots)
            
            // Set form data
            setFormData(prev => ({
                ...prev,
                vendor_id: schedule.vendor_id?.toString() || '',
                start_date: formatDateForInput(schedule.start_date),
                end_date: formatDateForInput(schedule.end_date),
                min_lead_time_minutes: schedule.min_lead_time_minutes?.toString() || '',
                time_slot_duration: schedule.time_slot_duration?.toString() || '15',
                is_active: schedule.is_active !== undefined ? schedule.is_active : true
            }))
        }
    }, [scheduleData])

    const calculateDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return 0
        const [startHours, startMinutes] = startTime.split(':').map(Number)
        const [endHours, endMinutes] = endTime.split(':').map(Number)
        return (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes)
    }

    const roundTimeToSlot = (timeString, slotDuration, roundUp = false) => {
        if (!timeString) return ''
        
        const [hours, minutes] = timeString.split(':').map(Number)
        const totalMinutes = hours * 60 + minutes
        const roundedMinutes = roundUp 
            ? Math.ceil(totalMinutes / slotDuration) * slotDuration
            : Math.floor(totalMinutes / slotDuration) * slotDuration
        
        const roundedHours = Math.floor(roundedMinutes / 60)
        const roundedMins = roundedMinutes % 60
        
        return `${roundedHours.toString().padStart(2, '0')}:${roundedMins.toString().padStart(2, '0')}`
    }

    const validateEndTime = (startTime, endTime, slotDuration) => {
        if (!startTime || !endTime) return true
        
        const [startHours, startMinutes] = startTime.split(':').map(Number)
        const [endHours, endMinutes] = endTime.split(':').map(Number)
        
        const startTotal = startHours * 60 + startMinutes
        const endTotal = endHours * 60 + endMinutes
        
        if (endTotal <= startTotal) return false
        
        const duration = endTotal - startTotal
        return duration % slotDuration === 0
    }

    const calculateSuggestedEndTime = (startTime, slotDuration) => {
        if (!startTime) return ''
        
        const [hours, minutes] = startTime.split(':').map(Number)
        const totalMinutes = hours * 60 + minutes + slotDuration
        const endHours = Math.floor(totalMinutes / 60)
        const endMinutes = totalMinutes % 60
        
        return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`
    }

    const generateTimeSlots = () => {
        if (!formData.start_date || !formData.end_date || !formData.time_slot_duration) {
            return []
        }

        const slotDuration = parseInt(formData.time_slot_duration)
        const scheduleStart = new Date(formData.start_date)
        const scheduleEnd = new Date(formData.end_date)
        
        const startHour = scheduleStart.getHours()
        const startMinute = scheduleStart.getMinutes()
        const endHour = scheduleEnd.getHours()
        const endMinute = scheduleEnd.getMinutes()

        const slots = []
        let currentTime = startHour * 60 + startMinute
        const endTime = endHour * 60 + endMinute

        while (currentTime + slotDuration <= endTime) {
            const startHours = Math.floor(currentTime / 60)
            const startMinutes = currentTime % 60
            const endHours = Math.floor((currentTime + slotDuration) / 60)
            const endMinutes = (currentTime + slotDuration) % 60
            
            slots.push({
                start: `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`,
                end: `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`,
                label: `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')} - ${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`
            })
            
            currentTime += slotDuration
        }

        return slots
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleSlotChange = (index, field, value) => {
        const slotDuration = parseInt(formData.time_slot_duration)
        const updatedSlots = [...formData.slots]
        
        if (field === 'start_time' && value) {
            const roundedStart = roundTimeToSlot(value, slotDuration, false)
            updatedSlots[index] = { 
                ...updatedSlots[index], 
                start_time: roundedStart
            }
        } else if (field === 'end_time' && value) {
            const roundedEnd = roundTimeToSlot(value, slotDuration, true)
            updatedSlots[index] = { 
                ...updatedSlots[index], 
                end_time: roundedEnd
            }
        } else {
            updatedSlots[index] = { ...updatedSlots[index], [field]: value }
        }
        
        setFormData({ ...formData, slots: updatedSlots })
    }

    const handleTimePeriodSelect = (index, timeSlot) => {
        const slotDuration = parseInt(formData.time_slot_duration)
        const updatedSlots = [...formData.slots]
        
        updatedSlots[index] = {
            ...updatedSlots[index],
            start_time: timeSlot.start,
            end_time: timeSlot.end
        }
        
        setFormData({ ...formData, slots: updatedSlots })
        setSelectedTimePeriod('')
    }

    const addNewSlot = () => {
        setFormData({
            ...formData,
            slots: [
                ...formData.slots,
                {
                    start_time: '',
                    end_time: '',
                    slot_limit: '5',
                    slot_status: 'Open'
                }
            ]
        })
    }

    const removeSlot = (index) => {
        if (formData.slots.length > 1) {
            const updatedSlots = formData.slots.filter((_, i) => i !== index)
            setFormData({ ...formData, slots: updatedSlots })
        }
    }

    const handleAddSlotToSchedule = (index) => {
        const slot = formData.slots[index]
        const slotDuration = parseInt(formData.time_slot_duration)
        
        // Validate all required fields
        if (!slot.start_time || !slot.end_time || !slot.slot_limit) {
            toast.error('Please fill all required fields for the slot')
            return
        }

        // Validate time format and range
        const [startHours, startMinutes] = slot.start_time.split(':').map(Number)
        const [endHours, endMinutes] = slot.end_time.split(':').map(Number)
        
        const startTotal = startHours * 60 + startMinutes
        const endTotal = endHours * 60 + endMinutes
        
        if (endTotal <= startTotal) {
            toast.error('End time must be after start time')
            return
        }

        // Validate duration is multiple of slot duration
        const duration = endTotal - startTotal
        if (duration % slotDuration !== 0) {
            toast.error(`Slot duration must be in multiples of ${slotDuration} minutes`)
            return
        }

        // Check if slot overlaps with any existing slot
        const overlaps = addedSlots.some(addedSlot => {
            // Skip checking if we're editing this same slot
            if (slot.id && addedSlot.id === slot.id) return false
            
            const [addedStartHours, addedStartMinutes] = addedSlot.start_time.split(':').map(Number)
            const [addedEndHours, addedEndMinutes] = addedSlot.end_time.split(':').map(Number)
            
            const addedStartTotal = addedStartHours * 60 + addedStartMinutes
            const addedEndTotal = addedEndHours * 60 + addedEndMinutes
            
            // Check for overlap
            return (startTotal < addedEndTotal && endTotal > addedStartTotal)
        })

        if (overlaps) {
            toast.error('This time slot overlaps with an existing slot in the schedule')
            return
        }

        // Check if slot is within schedule bounds
        if (formData.start_date && formData.end_date) {
            const scheduleStart = new Date(formData.start_date)
            const scheduleEnd = new Date(formData.end_date)
            
            const scheduleStartHour = scheduleStart.getHours()
            const scheduleStartMinute = scheduleStart.getMinutes()
            const scheduleEndHour = scheduleEnd.getHours()
            const scheduleEndMinute = scheduleEnd.getMinutes()
            
            const scheduleStartTotal = scheduleStartHour * 60 + scheduleStartMinute
            const scheduleEndTotal = scheduleEndHour * 60 + scheduleEndMinute
            
            if (startTotal < scheduleStartTotal || endTotal > scheduleEndTotal) {
                toast.error('Time slot must be within the schedule start and end times')
                return
            }
        }

        // Create or update slot
        const newAddedSlot = {
            ...slot,
            id: slot.id || Date.now() + index,
            duration: duration,
            isEditing: false // Remove editing flag
        }
        
        if (slot.id) {
            // Update existing slot
            setAddedSlots(prev => prev.map(s => 
                s.id === slot.id ? newAddedSlot : s
            ).sort((a, b) => {
                const [aHours, aMinutes] = a.start_time.split(':').map(Number)
                const [bHours, bMinutes] = b.start_time.split(':').map(Number)
                return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes)
            }))
            
            // Remove the slot from form data since it's been updated
            const updatedFormSlots = formData.slots.filter((_, i) => i !== index)
            setFormData({ ...formData, slots: updatedFormSlots })
            
            toast.success('Slot updated successfully')
        } else {
            // Add new slot
            setAddedSlots(prev => [...prev, newAddedSlot].sort((a, b) => {
                const [aHours, aMinutes] = a.start_time.split(':').map(Number)
                const [bHours, bMinutes] = b.start_time.split(':').map(Number)
                return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes)
            }))
            
            // Reset the form slot
            const updatedSlots = [...formData.slots]
            updatedSlots[index] = {
                start_time: '',
                end_time: '',
                slot_limit: '5',
                slot_status: 'Open'
            }
            setFormData({ ...formData, slots: updatedSlots })
            
            toast.success('Slot added to schedule')
        }
        
        setSelectedTimePeriod('')
    }

    const removeAddedSlot = (id) => {
        setAddedSlots(prev => prev.filter(slot => slot.id !== id))
        toast.success('Slot removed from schedule')
    }

    const editAddedSlot = (slot) => {
        // Check if this slot is already being edited in the form
        const isAlreadyInForm = formData.slots.some(s => s.id === slot.id);
        
        if (isAlreadyInForm) {
            // If already in form, just focus on it
            toast.info('Slot is already in edit mode');
            return;
        }
        
        // Add to form slots for editing (at the first position for better UX)
        setFormData(prev => ({
            ...prev,
            slots: [{
                id: slot.id,
                start_time: slot.start_time,
                end_time: slot.end_time,
                slot_limit: slot.slot_limit,
                slot_status: slot.slot_status
            }, ...prev.slots]
        }));
        
        // Mark the slot as being edited in addedSlots
        setAddedSlots(prev => prev.map(s => 
            s.id === slot.id ? { ...s, isEditing: true } : s
        ));
        
        toast.success('Slot loaded for editing');
    }

    const cancelEdit = (slotId) => {
        // Remove from form slots
        setFormData(prev => ({
            ...prev,
            slots: prev.slots.filter(s => s.id !== slotId)
        }));
        
        // Remove editing flag from addedSlots
        setAddedSlots(prev => prev.map(s => 
            s.id === slotId ? { ...s, isEditing: false } : s
        ));
        
        toast.info('Edit cancelled');
    }

    // Update schedule mutation
    const updateMutation = useMutation({
        mutationFn: async (data) => {
            const response = await api.put(`/api/order-schedule/${id}`, data);
            return response.data;
        },
        onSuccess: (responseData) => {
            queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
            queryClient.invalidateQueries({ queryKey: ['order-schedule', id] });
            toast.success('Order schedule updated successfully');
            navigate('/order-schedule');
        },
        onError: (error) => {
            const message = error.response?.data?.message || error.message || 'Failed to update schedule';
            toast.error(message);
            console.error('Update schedule error:', error);
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault()
        
        if (addedSlots.length === 0) {
            toast.error('Please add at least one time slot to the schedule')
            return
        }

        // Validate required fields
        if (!formData.vendor_id || !formData.start_date || !formData.end_date || !formData.min_lead_time_minutes) {
            toast.error('Please fill all required fields')
            return
        }

        // Prepare data for update
        const submitData = {
            vendor_id: formData.vendor_id ? parseInt(formData.vendor_id) : null,
            start_date: formData.start_date
                ? new Date(formData.start_date).toISOString()
                : null,
            end_date: formData.end_date
                ? new Date(formData.end_date).toISOString()
                : null,
            is_active: formData.is_active,
            min_lead_time_minutes: parseInt(formData.min_lead_time_minutes) || 0,
            time_slot_duration: parseInt(formData.time_slot_duration) || 15,
            slots: addedSlots.map(slot => ({
                id: slot.id,
                start_time: slot.start_time,
                end_time: slot.end_time,
                slot_limit: parseInt(slot.slot_limit) || 5,
                slot_status: slot.slot_status || 'Open'
            }))
        }

        updateMutation.mutate(submitData)
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        )
    }

    const availableTimeSlots = generateTimeSlots()

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <div className="flex items-center space-x-3 mb-2">
                    <button
                        onClick={() => navigate('/order-schedule')}
                        className="p-2 text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <h1 className="text-2xl font-bold text-secondary-900">Edit Order Schedule</h1>
                </div>
                <p className="text-secondary-600 pl-11">
                    Update delivery windows, manage time slots, and monitor scheduled orders.
                </p>
            </div>

            {/* Main Form */}
            <div className="bg-white rounded-lg border border-secondary-200 p-6">
                <form onSubmit={handleSubmit}>
                    <div className="space-y-6">
                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                    Start Date *
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                                    <input
                                        type="datetime-local"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                    End Date *
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                                    <input
                                        type="datetime-local"
                                        name="end_date"
                                        value={formData.end_date}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                    Minimum Lead Time (minutes) *
                                </label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                                    <input
                                        type="number"
                                        name="min_lead_time_minutes"
                                        value={formData.min_lead_time_minutes}
                                        onChange={handleChange}
                                        min="5"
                                        max="2880"
                                        placeholder="e.g., 30"
                                        className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                    Time Slot Duration (minutes) *
                                </label>
                                <select
                                    name="time_slot_duration"
                                    value={formData.time_slot_duration}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                                    required
                                >
                                    <option value="15">⏱️ 15 minutes</option>
                                    <option value="30">⏱️ 30 minutes</option>
                                    <option value="60">⏱️ 60 minutes</option>
                                    <option value="90">⏱️ 90 minutes</option>
                                    <option value="120">⏱️ 120 minutes</option>
                                </select>
                            </div>

                            <div className="md:col-span-2">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm font-medium text-secondary-900">Active Schedule</span>
                                </label>
                            </div>
                        </div>

                        {/* Time Slots Configuration */}
                        <div className="border-t border-secondary-200 pt-6">
                            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Configure Time Slots</h3>
                            
                            {formData.slots.map((slot, index) => {
                                const isEndTimeValid = validateEndTime(
                                    slot.start_time, 
                                    slot.end_time, 
                                    parseInt(formData.time_slot_duration)
                                )
                                const suggestedEndTime = calculateSuggestedEndTime(
                                    slot.start_time, 
                                    parseInt(formData.time_slot_duration)
                                )
                                
                                return (
                                    <div key={slot.id || index} className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 mb-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="font-medium text-secondary-900">
                                                {slot.id ? 'Edit Slot' : 'Add New Slot'}
                                            </h4>
                                            {slot.id && (
                                                <button
                                                    type="button"
                                                    onClick={() => cancelEdit(slot.id)}
                                                    className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                                                >
                                                    Cancel Edit
                                                </button>
                                            )}
                                        </div>
                                        
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                            {/* Quick Select */}
                                            {availableTimeSlots.length > 0 && (
                                                <div>
                                                    <label className="block text-sm font-medium text-secondary-900 mb-2">
                                                        Quick Select Time Period
                                                    </label>
                                                    <select
                                                        value={selectedTimePeriod}
                                                        onChange={(e) => {
                                                            const selectedSlot = availableTimeSlots.find(s => s.start === e.target.value)
                                                            if (selectedSlot) {
                                                                handleTimePeriodSelect(index, selectedSlot)
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors text-sm"
                                                    >
                                                        <option value="">Select from available slots</option>
                                                        {availableTimeSlots.map((timeSlot, idx) => (
                                                            <option key={idx} value={timeSlot.start}>
                                                                {timeSlot.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                                    Start Time *
                                                </label>
                                                <input
                                                    type="time"
                                                    value={slot.start_time}
                                                    onChange={(e) => handleSlotChange(index, 'start_time', e.target.value)}
                                                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                                                    step={parseInt(formData.time_slot_duration) * 60}
                                                />
                                                {slot.start_time && suggestedEndTime && (
                                                    <p className="text-xs text-blue-600 mt-1">
                                                        Suggested: {suggestedEndTime}
                                                    </p>
                                                )}
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                                    End Time *
                                                </label>
                                                <input
                                                    type="time"
                                                    value={slot.end_time}
                                                    onChange={(e) => handleSlotChange(index, 'end_time', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none transition-colors ${
                                                        slot.end_time && !isEndTimeValid 
                                                            ? 'border-red-300 focus:border-red-500' 
                                                            : 'border-secondary-300 focus:border-primary-500'
                                                    }`}
                                                    step={parseInt(formData.time_slot_duration) * 60}
                                                />
                                                {slot.end_time && !isEndTimeValid && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                        Must be in {formData.time_slot_duration}-minute intervals
                                                    </p>
                                                )}
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                                    Max Orders *
                                                </label>
                                                <div className="relative">
                                                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                                                    <input
                                                        type="number"
                                                        value={slot.slot_limit}
                                                        onChange={(e) => handleSlotChange(index, 'slot_limit', e.target.value)}
                                                        min="1"
                                                        max="50"
                                                        placeholder='e.g., 5'
                                                        className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-secondary-900 mb-2">
                                                    Slot Status *
                                                </label>
                                                <select
                                                    value={slot.slot_status}
                                                    onChange={(e) => handleSlotChange(index, 'slot_status', e.target.value)}
                                                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                                                >
                                                    <option value="Open">✅ Open</option>
                                                    <option value="Closed">❌ Closed</option>
                                                    <option value="Paused">⏸️ Paused</option>
                                                </select>
                                            </div>
                                        </div>
                                        
                                        <div className="flex justify-end space-x-2 mt-4">
                                            <button
                                                type="button"
                                                onClick={() => handleAddSlotToSchedule(index)}
                                                disabled={!slot.start_time || !slot.end_time || !slot.slot_limit || !isEndTimeValid}
                                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                    !slot.start_time || !slot.end_time || !slot.slot_limit || !isEndTimeValid
                                                        ? 'bg-secondary-200 text-secondary-500 cursor-not-allowed'
                                                        : 'bg-primary-600 text-white hover:bg-primary-700'
                                                }`}
                                            >
                                                {slot.id ? 'Update Slot' : 'Add to Schedule'}
                                            </button>
                                            {formData.slots.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeSlot(index)}
                                                    className="px-4 py-2 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                                                >
                                                    Remove Field
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                            
                            <button
                                type="button"
                                onClick={addNewSlot}
                                className="w-full py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 border border-secondary-300 transition-colors"
                            >
                                <Plus className="inline h-4 w-4 mr-2" />
                                Add Another Slot Field
                            </button>
                        </div>

                        {/* Added Slots Display */}
                        {addedSlots.length > 0 && (
                            <div className="border-t border-secondary-200 pt-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-secondary-900">
                                        Scheduled Time Slots ({addedSlots.length} slots)
                                    </h3>
                                    <div className="text-sm text-secondary-600">
                                        Total Capacity: {addedSlots.reduce((sum, slot) => sum + parseInt(slot.slot_limit), 0)}
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {addedSlots.map((slot) => {
                                        const duration = slot.duration || 
                                            ((parseInt(slot.end_time.split(':')[0]) * 60 + parseInt(slot.end_time.split(':')[1])) - 
                                             (parseInt(slot.start_time.split(':')[0]) * 60 + parseInt(slot.start_time.split(':')[1])))
                                        
                                        return (
                                            <div key={slot.id} className={`flex items-center justify-between rounded-lg p-4 border transition-colors ${
                                                slot.isEditing 
                                                    ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' 
                                                    : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                                            }`}>
                                                <div className="flex-1">
                                                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                                                        <div className="flex items-center space-x-2">
                                                            <Clock className="h-5 w-5 text-blue-600" />
                                                            <div>
                                                                <span className="font-medium">{slot.start_time} - {slot.end_time}</span>
                                                                <span className="ml-2 text-sm text-blue-600">({duration} mins)</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-4">
                                                            <div className="text-sm text-secondary-700">
                                                                <Users className="inline h-4 w-4 mr-1" />
                                                                Max: <span className="font-medium">{slot.slot_limit}</span>
                                                            </div>
                                                            <div className="text-sm">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                                    slot.slot_status === 'Open' 
                                                                        ? 'bg-green-100 text-green-800' 
                                                                        : slot.slot_status === 'Closed' 
                                                                        ? 'bg-red-100 text-red-800' 
                                                                        : 'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                    {slot.slot_status}
                                                                </span>
                                                            </div>
                                                            {slot.isEditing && (
                                                                <span className="text-sm text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                                                                    ✏️ Editing
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex space-x-2">
                                                    {!slot.isEditing && (
                                                        <button
                                                            type="button"
                                                            onClick={() => editAddedSlot(slot)}
                                                            className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm transition-colors"
                                                        >
                                                            <Edit className="h-4 w-4 mr-1 inline" />
                                                            Edit
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAddedSlot(slot.id)}
                                                        className="ml-4 p-2 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Remove slot"
                                                    >
                                                        <X className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Form Actions */}
                    <div className="border-t border-secondary-200 pt-6 mt-6 flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={() => navigate('/order-schedule')}
                            className="px-6 py-2 border border-secondary-300 text-secondary-700 rounded-lg hover:bg-secondary-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={addedSlots.length === 0 || updateMutation.isLoading}
                            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                addedSlots.length === 0
                                    ? 'bg-secondary-300 text-secondary-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800'
                            }`}
                        >
                            {updateMutation.isLoading ? (
                                <>
                                    <Loader2 className="inline h-4 w-4 mr-2 animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                'Update Order Schedule'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default OrderscheduleEdit